import { Icon } from "customer-portal-ui-component-library";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { CopyBlock } from "react-code-blocks";
import ReactMarkdown from 'react-markdown'

import { useFetchBulletinQuery } from "../../../redux/bulletinApi";
import { AffectedProduct } from "../../../redux/models/BulletinModel";

export const MoreDetails = () => {
  const { id } = useParams();
  const { data } = useFetchBulletinQuery(Number(id));

  // Replace all instances of '<a' with a modified '<a' tag that has custom CSS styles.
  const renderShortDescription = (description: string) => {
    const descriptionWithStyles = description.replace(
      /<a /g,
      '<a style="color: #00AD99; text-decoration: none;" '
    );
    return { __html: descriptionWithStyles };
  };

  // Convert <a> tags returned in product steps to markdown links
  const convertATagsToMarkdown = (text: string): string => {
    const regex = /<a href="(.*?)">(.*?)<\/a>/g;
    console.log(text.replace(regex, (match, p1, p2) => `[${p2}](${p1})`));
    return text.replace(regex, (match, p1, p2) => `[${p2}](${p1})`);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString().replace(/,/g, "");
  };

  return (
    <>
      <Box sx={{ pt: "25px", pb: "50px" }}>
        {data && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography sx={{ pb: 1 }} variant="h2" fontWeight={100}>{data.title}</Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Published Date: {formatDate(data.publishedDate)}
              </Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Updated Date: {formatDate(data.updatedDate)}
              </Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Status: {data.status}
              </Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Category: {data.category}
              </Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Severity: {data.severity}
              </Typography>
              <Typography variant='h4' fontWeight={100} color="textSecondary">
                Author: {data.author}
              </Typography>
              <br />
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={renderShortDescription(
                  data.longDescription
                )}
              />
            </CardContent>
            <Accordion>
              <AccordionSummary
                expandIcon={<Icon name='chevron-down' variant='light' fontSize='small' />}
              >
                <Typography variant="h5">Affected Products</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "15%" }}>Article Id</TableCell>
                        <TableCell sx={{ width: "25%" }}>Product</TableCell>
                        <TableCell sx={{ width: "20%" }}>CVE Status</TableCell>
                        <TableCell sx={{ width: "45%" }}>Remediation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.affectedProducts.map((product: AffectedProduct) => (
                        <TableRow
                          key={product.id}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <TableCell>{product.articleId}</TableCell>
                          <TableCell>{product.product}</TableCell>
                          <TableCell>{product.cveStatus}</TableCell>
                          <TableCell>{product.remediation}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
            {data.affectedProducts.some(
              (product) => product.steps.length > 0
            ) && (
              <Accordion>
                <AccordionSummary
                  expandIcon={
                    <Icon name='chevron-down' variant='light' fontSize='small' />
                  }
                >
                  <Typography variant="h5">Product Steps</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {data.affectedProducts.map((product: AffectedProduct) => (
                    <>
                    {product.steps.length > 0 &&
                      <Typography variant="h5">
                        {product.product}
                      </Typography>
                    }
                    <List key={product.id}>
                      {product.steps.map((step) => (
                        <ListItem
                          key={step.id}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{ fontWeight: "bold" }}
                                variant="body2"
                                color="text.primary"
                              >
                              <span>{step.stepNumber}) </span>
                              <ReactMarkdown 
                                components={{ 
                                  // Render paragraph elements as span to keep them inline
                                  p: ({node, ...props}) => <span {...props} /> 
                                }}
                              >
                                {convertATagsToMarkdown(step.description)}
                              </ReactMarkdown>
                              </Typography>
                            }
                            secondary={
                              !step.snippet.includes("N/A") &&
                                <CopyBlock
                                  language="shell"
                                  text={step.snippet}
                                  codeBlock
                                  showLineNumbers={false}
                                />
                            }
                          />
                        </ListItem>
                      ))
                      }
                    </List>
                    </>
                    ))}
                </AccordionDetails>
              </Accordion>
            )}
            <br />
            <Box sx={{ padding: "10px" }}>
              {data.tags.map((tag, index) => (
                <Chip key={index} label={tag} sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Card>
        )}
        <Link to="/" style={{ textDecoration: "none" }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#00AD99",
              "&:hover": {
                color: "#00675b",
              },
            }}
          >
            BACK
          </Typography>
        </Link>
      </Box>
    </>
  );
};